import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Users, Award, ExternalLink, Shield, BookOpen, Stethoscope, GraduationCap, Globe } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScientificCouncil() {
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${API_URL}/api/track/visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: 'scientific-council', language })
    }).catch(() => {});
  }, [language]);

  const content = {
    fr: {
      badge: "Garantie des Normes",
      title: "Conseil Scientifique & PNS",
      subtitle: "Pharma Nuclear Services garantit les standards internationaux de qualité",
      pnsTitle: "PNS - Pharma Nuclear Services",
      pnsDescription: "Pharma Nuclear Services est notre partenaire exclusif pour la garantie de la qualité médicale. PNS assure le respect des normes hospitalières internationales, la certification des équipements et la formation continue du personnel médical.",
      pnsFeatures: [
        "Respect des normes hospitalières internationales",
        "Certification des équipements médicaux",
        "Formation continue du personnel",
        "Transfert de compétences",
        "Audit qualité régulier"
      ],
      councilTitle: "Conseil Scientifique",
      councilDescription: "Notre Conseil Scientifique réunit des experts internationalement reconnus qui garantissent l'excellence médicale et scientifique de nos projets.",
      directoryButton: "Consulter le Directoire Scientifique & les Profils des Experts",
      members: [
        { name: "Prof. Patrick PAULUS", role: "Président du Conseil Scientifique", specialty: "Médecine Nucléaire" },
        { name: "Prof. Denis GUILLOTTEAU", role: "Expert en Médecine Nucléaire", specialty: "Radiopharmaceutiques" },
        { name: "Prof. Antoine COQUEREL", role: "Spécialiste Infrastructures Hospitalières", specialty: "Conception Hospitalière" },
        { name: "Dr Paolo BEDOGNI", role: "Conseiller Médical International", specialty: "Santé Publique" }
      ]
    },
    en: {
      badge: "Standards Guarantee",
      title: "Scientific Council & PNS",
      subtitle: "Pharma Nuclear Services guarantees international quality standards",
      pnsTitle: "PNS - Pharma Nuclear Services",
      pnsDescription: "Pharma Nuclear Services is our exclusive partner for medical quality assurance. PNS ensures compliance with international hospital standards, equipment certification and continuous training of medical staff.",
      pnsFeatures: [
        "Compliance with international hospital standards",
        "Medical equipment certification",
        "Continuous staff training",
        "Skills transfer",
        "Regular quality audit"
      ],
      councilTitle: "Scientific Council",
      councilDescription: "Our Scientific Council brings together internationally recognized experts who guarantee the medical and scientific excellence of our projects.",
      directoryButton: "Consult the Scientific Directory & Expert Profiles",
      members: [
        { name: "Prof. Patrick PAULUS", role: "President of the Scientific Council", specialty: "Nuclear Medicine" },
        { name: "Prof. Denis GUILLOTTEAU", role: "Nuclear Medicine Expert", specialty: "Radiopharmaceuticals" },
        { name: "Prof. Antoine COQUEREL", role: "Hospital Infrastructure Specialist", specialty: "Hospital Design" },
        { name: "Dr Paolo BEDOGNI", role: "International Medical Advisor", specialty: "Public Health" }
      ]
    },
    pt: {
      badge: "Garantia de Normas",
      title: "Conselho Científico & PNS",
      subtitle: "Pharma Nuclear Services garante os padrões internacionais de qualidade",
      pnsTitle: "PNS - Pharma Nuclear Services",
      pnsDescription: "Pharma Nuclear Services é nosso parceiro exclusivo para garantia de qualidade médica. PNS assegura o cumprimento das normas hospitalares internacionais, certificação de equipamentos e formação contínua do pessoal médico.",
      pnsFeatures: [
        "Cumprimento das normas hospitalares internacionais",
        "Certificação de equipamentos médicos",
        "Formação contínua do pessoal",
        "Transferência de competências",
        "Auditoria de qualidade regular"
      ],
      councilTitle: "Conselho Científico",
      councilDescription: "Nosso Conselho Científico reúne especialistas internacionalmente reconhecidos que garantem a excelência médica e científica dos nossos projetos.",
      directoryButton: "Consultar o Diretório Científico & Perfis dos Especialistas",
      members: [
        { name: "Prof. Patrick PAULUS", role: "Presidente do Conselho Científico", specialty: "Medicina Nuclear" },
        { name: "Prof. Denis GUILLOTTEAU", role: "Especialista em Medicina Nuclear", specialty: "Radiofármacos" },
        { name: "Prof. Antoine COQUEREL", role: "Especialista em Infraestruturas Hospitalares", specialty: "Conceção Hospitalar" },
        { name: "Dr Paolo BEDOGNI", role: "Consultor Médico Internacional", specialty: "Saúde Pública" }
      ]
    },
    it: {
      badge: "Garanzia degli Standard",
      title: "Consiglio Scientifico & PNS",
      subtitle: "Pharma Nuclear Services garantisce gli standard internazionali di qualità",
      pnsTitle: "PNS - Pharma Nuclear Services",
      pnsDescription: "Pharma Nuclear Services è il nostro partner esclusivo per la garanzia della qualità medica. PNS assicura il rispetto degli standard ospedalieri internazionali, la certificazione delle attrezzature e la formazione continua del personale medico.",
      pnsFeatures: [
        "Rispetto degli standard ospedalieri internazionali",
        "Certificazione delle attrezzature mediche",
        "Formazione continua del personale",
        "Trasferimento di competenze",
        "Audit qualità regolare"
      ],
      councilTitle: "Consiglio Scientifico",
      councilDescription: "Il nostro Consiglio Scientifico riunisce esperti riconosciuti a livello internazionale che garantiscono l'eccellenza medica e scientifica dei nostri progetti.",
      directoryButton: "Consultare il Direttorio Scientifico e i Profili degli Esperti",
      members: [
        { name: "Prof. Patrick PAULUS", role: "Presidente del Consiglio Scientifico", specialty: "Medicina Nucleare" },
        { name: "Prof. Denis GUILLOTTEAU", role: "Esperto in Medicina Nucleare", specialty: "Radiofarmaci" },
        { name: "Prof. Antoine COQUEREL", role: "Specialista Infrastrutture Ospedaliere", specialty: "Progettazione Ospedaliera" },
        { name: "Dr Paolo BEDOGNI", role: "Consulente Medico Internazionale", specialty: "Sanità Pubblica" }
      ]
    },
    ru: {
      badge: "Гарантия Стандартов",
      title: "Научный Совет и PNS",
      subtitle: "Pharma Nuclear Services гарантирует международные стандарты качества",
      pnsTitle: "PNS - Pharma Nuclear Services",
      pnsDescription: "Pharma Nuclear Services — наш эксклюзивный партнёр по гарантии качества медицины. PNS обеспечивает соблюдение международных больничных стандартов, сертификацию оборудования и непрерывное обучение медицинского персонала.",
      pnsFeatures: [
        "Соблюдение международных больничных стандартов",
        "Сертификация медицинского оборудования",
        "Непрерывное обучение персонала",
        "Передача компетенций",
        "Регулярный аудит качества"
      ],
      councilTitle: "Научный Совет",
      councilDescription: "Наш Научный совет объединяет международно признанных экспертов, гарантирующих медицинское и научное превосходство наших проектов.",
      directoryButton: "Консультировать научный справочник и профили экспертов",
      members: [
        { name: "Проф. Патрик ПАУЛУС", role: "Президент Научного совета", specialty: "Ядерная медицина" },
        { name: "Проф. Дени ГИЙОТО", role: "Эксперт по ядерной медицине", specialty: "Радиофармпрепараты" },
        { name: "Проф. Антуан КОКЕРЕЛЬ", role: "Специалист по больничной инфраструктуре", specialty: "Проектирование больниц" },
        { name: "Д-р Паоло БЕДОНЬИ", role: "Международный медицинский консультант", specialty: "Общественное здравоохранение" }
      ]
    },
    zh: {
      badge: "标准保证",
      title: "科学委员会与PNS",
      subtitle: "Pharma Nuclear Services保证国际质量标准",
      pnsTitle: "PNS - Pharma Nuclear Services",
      pnsDescription: "Pharma Nuclear Services是我们医疗质量保证的独家合作伙伴。PNS确保遵守国际医院标准、设备认证和医务人员持续培训。",
      pnsFeatures: [
        "遵守国际医院标准",
        "医疗设备认证",
        "员工持续培训",
        "技能转移",
        "定期质量审计"
      ],
      councilTitle: "科学委员会",
      councilDescription: "我们的科学委员会汇集了国际公认的专家，保证我们项目的医疗和科学卓越性。",
      directoryButton: "查阅科学名录和专家简介",
      members: [
        { name: "Patrick PAULUS 教授", role: "科学委员会主席", specialty: "核医学" },
        { name: "Denis GUILLOTTEAU 教授", role: "核医学专家", specialty: "放射性药物" },
        { name: "Antoine COQUEREL 教授", role: "医院基础设施专家", specialty: "医院设计" },
        { name: "Paolo BEDOGNI 博士", role: "国际医学顾问", specialty: "公共卫生" }
      ]
    }
  };

  const c = content[language] || content.fr;

  return (
    <div className="min-h-screen bg-white" data-testid="scientific-council-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-sky-900 via-slate-800 to-slate-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDUpIiBzdHJva2Utd2lkdGg9IjIiLz48L2c+PC9zdmc+')] opacity-30"></div>
        
        <div className="max-w-6xl mx-auto px-6 relative z-10 text-center">
          <div className="inline-block px-4 py-2 bg-sky-600/20 border border-sky-400/30 rounded-full backdrop-blur-sm mb-6">
            <p className="text-sm font-medium text-sky-300 uppercase tracking-wider flex items-center justify-center gap-2">
              <Shield className="w-4 h-4" />
              {c.badge}
            </p>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
            {c.title}
          </h1>
          
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-8">{c.subtitle}</p>

          {/* BOUTON PREMIUM PNS - Immédiatement visible */}
          <a
            href="https://www.pharmanuclearservices.com/blank-1"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pns-directory-btn-hero"
            className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-bold text-lg rounded-full transition-all hover:scale-105 shadow-xl hover:shadow-2xl"
          >
            <Globe className="w-6 h-6" />
            {c.directoryButton}
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* PNS Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-white border border-slate-200 rounded-3xl p-8 md:p-12 shadow-lg">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center">
                <Stethoscope className="w-8 h-8 text-sky-600" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{fontFamily: 'Playfair Display, serif'}}>
                  {c.pnsTitle}
                </h2>
                <p className="text-sm text-sky-600 font-medium">Pharma Nuclear Services</p>
              </div>
            </div>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-8">{c.pnsDescription}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {c.pnsFeatures.map((feature, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Award className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="text-sm text-slate-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Scientific Council Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
              {c.councilTitle}
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">{c.councilDescription}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {c.members.map((member, i) => (
              <div 
                key={i}
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:border-sky-300 transition-all"
                data-testid={`council-member-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-sky-500 to-emerald-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">{member.name}</h3>
                    <p className="text-sm text-sky-600 font-medium mb-2">{member.role}</p>
                    <p className="text-sm text-slate-500">{member.specialty}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* CTA Button - Point Focal */}
          <div className="text-center">
            <a
              href="https://www.pharmanuclearservices.com/blank-1"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pns-directory-btn"
              className="inline-flex items-center gap-3 px-10 py-5 bg-sky-600 hover:bg-sky-700 text-white font-bold text-lg rounded-full transition-all hover:scale-105 shadow-xl"
            >
              <Globe className="w-6 h-6" />
              {c.directoryButton}
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-lg text-slate-300">
            {language === 'fr' ? 'LENODA s\'appuie sur des experts de renommée internationale pour garantir l\'excellence de ses projets.' :
             language === 'en' ? 'LENODA relies on internationally renowned experts to guarantee the excellence of its projects.' :
             language === 'pt' ? 'LENODA conta com especialistas de renome internacional para garantir a excelência dos seus projetos.' :
             language === 'it' ? 'LENODA si affida a esperti di fama internazionale per garantire l\'eccellenza dei suoi progetti.' :
             language === 'ru' ? 'LENODA опирается на всемирно известных экспертов для гарантии превосходства своих проектов.' :
             'LENODA依靠国际知名专家来保证其项目的卓越性。'}
          </p>
        </div>
      </section>
    </div>
  );
}
